import { useState, useEffect, useCallback } from 'react';
import { getAuthHeader } from '../utils/authUtils';

/**
 * Loads the version stack for every document slot on an application.
 *
 * Uploads archive the file they replace rather than overwriting it, so each
 * slot (W-9, rate sheet, COI, per-policy COI) can hold several versions. This
 * hook fetches them and exposes a delete that keeps the list in sync.
 *
 * Returns { versions, loading, error, refresh, deleteVersion }
 *   versions: { w9: [], rateSheet: [], coi: [], policies: [{policyId, policyType, versions}] }
 */
export default function useDocumentVersions(applicationId) {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const [versions, setVersions] = useState({ w9: [], rateSheet: [], coi: [], policies: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/api/applications/${applicationId}/document-versions`, {
        headers: getAuthHeader()
      });
      if (!res.ok) throw new Error('Failed to load document versions');
      const data = await res.json();
      setVersions({
        w9: data.w9 || [],
        rateSheet: data.rateSheet || [],
        coi: data.coi || [],
        policies: data.policies || []
      });
    } catch (err) {
      console.error('[useDocumentVersions]', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, applicationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * @param {string} slot     'w9' | 'rateSheet' | 'coi' | 'policy'
   * @param {string} path     the stored path of the version to remove
   * @param {string} policyRef policy _id or policyType, required when slot==='policy'
   */
  const deleteVersion = useCallback(async (slot, path, policyRef) => {
    const res = await fetch(`${apiBaseUrl}/api/applications/${applicationId}/document-versions`, {
      method: 'DELETE',
      headers: getAuthHeader(),
      body: JSON.stringify({ slot, path, policyRef })
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Failed to delete document version');
    }
    const result = await res.json();
    await refresh();
    return result;
  }, [apiBaseUrl, applicationId, refresh]);

  return { versions, loading, error, refresh, deleteVersion };
}
